import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { BsInfoSquareFill } from 'react-icons/bs';
import { IoSave } from 'react-icons/io5';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { applicationAnimVariants } from '../../constants/animationVariants';
import {
  REQUIRED_DOC_NAMES,
  SELECT_FIELD_DEFAULT_VALUE,
} from '../../constants/settings';
import {
  additionalDocumentUploadSchema,
  mandatoryDocumentUploadSchema,
} from '../../schemas';
import { ApplicantDocument } from '../../types/applications';
import AddedDocumentTable from '../ApplicationDocComps/AddedDocumentTable';
import AcademiaButton from '../CommonComps/Buttons/AcademiaButton';
import AcademiaFileInputField from '../CommonComps/Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../CommonComps/Inputs/AcademiaFormSelect';
import AcademiaInputField from '../CommonComps/Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../CommonComps/Inputs/AcademiaTextAreaField';
import DocUploadInstructionModal from '../CommonComps/Modals/DocUploadInstructionModal';

const ApplicationDocumentInfo = () => {
  // integration of react hooks here
  const [addedMandatoryDocuments, setAddedMandatoryDocuments] = useState<
    Array<ApplicantDocument>
  >([]);
  const [addedAdditionalDocuments, setAddedAdditionalDocuments] = useState<
    Array<ApplicantDocument>
  >([]);

  const [showInstructionModal, setShowInstructionModal] =
    useState<boolean>(false);
  const [resetSelectField, setResetSelectField] = useState<boolean>(false);

  // handler function to handle mandatory document stacking declared here
  const addMandatoryDocumentHandler = (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    console.log(values);

    setAddedMandatoryDocuments((state) => [
      ...state,
      {
        document_name: values.document_name.name,
        document_file: values.document_file,
        notes: values.notes,
      },
    ]);

    setResetSelectField(true);

    actions.resetForm();
  };

  // handler function to handle additional document stacking declared here
  const addAdditionalDocumentHandler = (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    console.log(values);

    setAddedAdditionalDocuments((state) => [
      ...state,
      {
        document_name: values.add_document_name,
        document_file: values.add_document_file,
        notes: values.add_notes,
      },
    ]);

    setResetSelectField(true);

    actions.resetForm();
  };

  console.log(
    'ADDED DOCS: ',
    addedMandatoryDocuments,
    addedAdditionalDocuments
  );

  // rendering application document info component here
  return (
    <>
      <motion.div
        variants={applicationAnimVariants}
        initial='initial'
        animate='animate'
        className='px-5 py-4 shadow-academia-sm rounded-xl space-y-5'
      >
        <div className='flex items-center justify-between border-b-2 border-dashed border-base-orange pb-1 pr-2'>
          <h3 className='text-2xl font-light text-base-orange italic'>
            Mandatory Documents
          </h3>

          <button
            title='Check instructions'
            type='button'
            className='relative hover:opacity-70 active:scale-95 duration-300 rounded-md bg-base-white'
            onClick={() => setShowInstructionModal(true)}
          >
            <BsInfoSquareFill className='relative size-7 text-legal-dark-green bg-base-white z-10' />
            <span className='absolute top-0 left-0 block size-full bg-base-orange/40 animate-ping rounded-md' />
          </button>
        </div>

        <Formik
          initialValues={{
            document_name: SELECT_FIELD_DEFAULT_VALUE,
            document_file: '',
            notes: '',
          }}
          onSubmit={addMandatoryDocumentHandler}
          validationSchema={mandatoryDocumentUploadSchema}
        >
          {(props) => (
            <Form className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <AcademiaFormSelect
                  name='document_name'
                  label='Document Name'
                  options={REQUIRED_DOC_NAMES.filter(
                    (doc) =>
                      !addedMandatoryDocuments.some(
                        (addedDoc) => addedDoc.document_name === doc.name
                      )
                  )}
                  defaultValue={props.values.document_name}
                  resetSelectField={resetSelectField}
                  setResetSelectField={() => setResetSelectField(false)}
                  showSearchInput
                  isRequired
                />

                <AcademiaFileInputField
                  name='document_file'
                  label='Upload Document'
                  btnText='Upload'
                  accept='.docx, .pdf'
                  placeholder='Upload document here...'
                  resetField={resetSelectField}
                  // filePath={
                  //   retainAgreement ? retainAgreement.document : undefined
                  // }
                  isRequired
                />

                <div className='md:col-span-2'>
                  <AcademiaTextAreaField
                    name='notes'
                    label='Additional Notes'
                    placeholder='Enter additional notes here (if required)...'
                  />
                </div>
              </div>

              <div className='flex items-center justify-center'>
                <AcademiaButton
                  title={
                    props.values.document_name.id === -1
                      ? 'Please, select a document name first!'
                      : undefined
                  }
                  type='submit'
                  variant='info'
                  icon={<TbSquareRoundedPlusFilled className='size-6' />}
                  additionalClassNames='!py-2 !text-base w-full md:w-fit'
                  isDisabled={props.values.document_name.id === -1}
                >
                  Add Document
                </AcademiaButton>
              </div>
            </Form>
          )}
        </Formik>

        {addedMandatoryDocuments.length ? (
          <AddedDocumentTable
            addedDocuments={addedMandatoryDocuments}
            setAddedDocuments={setAddedMandatoryDocuments}
          />
        ) : null}
      </motion.div>

      <motion.div
        variants={applicationAnimVariants}
        initial='initial'
        animate='animate'
        className='px-5 py-4 shadow-academia-sm rounded-xl space-y-5'
      >
        <div className='flex items-center justify-between border-b-2 border-dashed border-base-orange pb-1 pr-2'>
          <h3 className='text-2xl font-light text-base-orange italic'>
            Additional Documents
          </h3>
        </div>

        <Formik
          initialValues={{
            add_document_name: '',
            add_document_file: '',
            add_notes: '',
          }}
          onSubmit={addAdditionalDocumentHandler}
          validationSchema={additionalDocumentUploadSchema}
        >
          {(props) => (
            <Form className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <AcademiaInputField
                  type='text'
                  name='add_document_name'
                  label='Document Name'
                  placeholder='Enter document name here...'
                  isRequired
                />

                <AcademiaFileInputField
                  name='add_document_file'
                  label='Upload Document'
                  btnText='Upload'
                  accept='.docx, .pdf'
                  placeholder='Upload document here...'
                  resetField={resetSelectField}
                  // filePath={
                  //   retainAgreement ? retainAgreement.document : undefined
                  // }
                  isRequired
                />

                <div className='md:col-span-2'>
                  <AcademiaTextAreaField
                    name='add_notes'
                    label='Additional Notes'
                    placeholder='Enter additional notes here (if required)...'
                  />
                </div>
              </div>

              <div className='flex items-center justify-center'>
                <AcademiaButton
                  type='submit'
                  variant='info'
                  icon={<TbSquareRoundedPlusFilled className='size-6' />}
                  additionalClassNames='!py-2 !text-base w-full md:w-fit'
                >
                  Add Document
                </AcademiaButton>
              </div>
            </Form>
          )}
        </Formik>

        {addedAdditionalDocuments.length ? (
          <AddedDocumentTable
            addedDocuments={addedAdditionalDocuments}
            setAddedDocuments={setAddedAdditionalDocuments}
          />
        ) : null}
      </motion.div>

      <div className='flex items-center justify-end mt-5 px-5 py-4 shadow-academia-sm rounded-xl'>
        <AcademiaButton
          type='button'
          variant='premium'
          icon={<IoSave className='size-6' />}
          additionalClassNames='w-full md:w-fit'
        >
          Save Application
        </AcademiaButton>
      </div>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showInstructionModal ? (
          <DocUploadInstructionModal
            handleModalClose={() => setShowInstructionModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default ApplicationDocumentInfo;
